import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Tab,
    Tabs,
    IconButton,
    CircularProgress,
    Alert,
    Snackbar,
    InputAdornment,
    MenuItem,
    Switch,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
    Language as WebsiteIcon,
    Upload as UploadIcon
} from '@mui/icons-material';
import { ColorPicker } from '../../business/CouponDesigner/ColorPicker';
import adminApi from '../../../services/adminApi';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const fontFamilies = ['Roboto', 'Open Sans', 'Montserrat', 'Lato'];

export default function Settings() {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [settings, setSettings] = useState({
        branding: {
            company: {
                name: '',
                logo: { url: '', width: 0, height: 0 },
                websiteUrl: '',
                socialLinks: {
                    facebook: '',
                    instagram: '',
                    twitter: '',
                    linkedin: ''
                }
            },
            theme: {
                primaryColor: '#1976d2',
                secondaryColor: '#dc004e',
                backgroundColor: '#ffffff',
                textColor: '#000000',
                fontFamily: 'Roboto'
            },
            landingPage: {
                defaultWelcomeHeading: 'Welcome Back!',
                defaultWelcomeMessage: 'You have already registered for this coupon. You can download it again below.',
                defaultButtonText: 'DOWNLOAD COUPON',
                defaultFooterText: ''
            }
        },
        analytics: {
            enabled: false,
            type: 'google_analytics',
            googleAnalytics: {
                measurementId: ''
            },
            googleTagManager: {
                containerId: ''
            }
        }
    });

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const response = await adminApi.getBrandingSettings();
            if (response.data.data) {
                setSettings(response.data.data);
            } else {
                setError('Invalid settings data received');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load settings');
            console.error('Error loading settings:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const dataToSend = {
                ...settings,
                analytics: {
                    enabled: settings.analytics?.enabled || false,
                    type: settings.analytics?.type || 'google_analytics',
                    googleAnalytics: {
                        measurementId: settings.analytics?.googleAnalytics?.measurementId || ''
                    },
                    googleTagManager: {
                        containerId: settings.analytics?.googleTagManager?.containerId || ''
                    }
                }
            };
            const response = await adminApi.updateBrandingSettings(dataToSend);
            if (response.data.status === 'success') {
                setSuccess('Settings saved successfully');
                setSettings(response.data.data);
            } else {
                setError('Failed to save settings');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save settings');
            console.error('Error saving settings:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('logo', file);

        try {
            setSaving(true);
            const response = await adminApi.uploadLogo(formData);
            if (response.data.status === 'success') {
                setSettings(prev => ({
                    ...prev,
                    branding: {
                        ...prev.branding,
                        company: {
                            ...prev.branding.company,
                            logo: response.data.data.branding.company.logo
                        }
                    }
                }));
                setSuccess('Logo uploaded successfully');
            } else {
                setError('Failed to upload logo');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to upload logo');
            console.error('Error uploading logo:', err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Typography variant="h4" sx={{ mb: 4 }}>Settings</Typography>

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={(_, newValue) => setTabValue(newValue)}
                    aria-label="settings tabs"
                >
                    <Tab label="Company Branding" />
                    <Tab label="Theme" />
                    <Tab label="Landing Page" />
                    <Tab label="Analytics" />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                value={settings.branding.company.name}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        company: {
                                            ...prev.branding.company,
                                            name: e.target.value
                                        }
                                    }
                                }))}
                                sx={{ mb: 3 }}
                            />

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Company Logo</Typography>
                                {settings.branding.company.logo?.url && (
                                    <Box sx={{ mb: 2 }}>
                                        <img
                                            src={settings.branding.company.logo.url}
                                            alt="Company Logo"
                                            style={{ 
                                                maxWidth: '100%',
                                                height: 'auto',
                                                maxHeight: 100,
                                                objectFit: 'contain',
                                                display: 'block'
                                            }}
                                            onError={(e) => {
                                                console.error('Error loading logo:', e);
                                                const img = e.target as HTMLImageElement;
                                                img.style.display = 'none';
                                            }}
                                        />
                                    </Box>
                                )}
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<UploadIcon />}
                                >
                                    Upload Logo
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                    />
                                </Button>
                            </Box>

                            <TextField
                                fullWidth
                                label="Website URL"
                                value={settings.branding.company.websiteUrl}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        company: {
                                            ...prev.branding.company,
                                            websiteUrl: e.target.value
                                        }
                                    }
                                }))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <WebsiteIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 3 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Social Media Links</Typography>
                            
                            <TextField
                                fullWidth
                                label="Facebook"
                                value={settings.branding.company.socialLinks.facebook}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        company: {
                                            ...prev.branding.company,
                                            socialLinks: {
                                                ...prev.branding.company.socialLinks,
                                                facebook: e.target.value
                                            }
                                        }
                                    }
                                }))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FacebookIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Instagram"
                                value={settings.branding.company.socialLinks.instagram}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        company: {
                                            ...prev.branding.company,
                                            socialLinks: {
                                                ...prev.branding.company.socialLinks,
                                                instagram: e.target.value
                                            }
                                        }
                                    }
                                }))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <InstagramIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Twitter"
                                value={settings.branding.company.socialLinks.twitter}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        company: {
                                            ...prev.branding.company,
                                            socialLinks: {
                                                ...prev.branding.company.socialLinks,
                                                twitter: e.target.value
                                            }
                                        }
                                    }
                                }))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <TwitterIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="LinkedIn"
                                value={settings.branding.company.socialLinks.linkedin}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        company: {
                                            ...prev.branding.company,
                                            socialLinks: {
                                                ...prev.branding.company.socialLinks,
                                                linkedin: e.target.value
                                            }
                                        }
                                    }
                                }))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LinkedInIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Primary Color</Typography>
                                <ColorPicker
                                    value={settings.branding.theme.primaryColor}
                                    onChange={(color) => setSettings(prev => ({
                                        ...prev,
                                        branding: {
                                            ...prev.branding,
                                            theme: {
                                                ...prev.branding.theme,
                                                primaryColor: color
                                            }
                                        }
                                    }))}
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Secondary Color</Typography>
                                <ColorPicker
                                    value={settings.branding.theme.secondaryColor}
                                    onChange={(color) => setSettings(prev => ({
                                        ...prev,
                                        branding: {
                                            ...prev.branding,
                                            theme: {
                                                ...prev.branding.theme,
                                                secondaryColor: color
                                            }
                                        }
                                    }))}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Background Color</Typography>
                                <ColorPicker
                                    value={settings.branding.theme.backgroundColor}
                                    onChange={(color) => setSettings(prev => ({
                                        ...prev,
                                        branding: {
                                            ...prev.branding,
                                            theme: {
                                                ...prev.branding.theme,
                                                backgroundColor: color
                                            }
                                        }
                                    }))}
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Text Color</Typography>
                                <ColorPicker
                                    value={settings.branding.theme.textColor}
                                    onChange={(color) => setSettings(prev => ({
                                        ...prev,
                                        branding: {
                                            ...prev.branding,
                                            theme: {
                                                ...prev.branding.theme,
                                                textColor: color
                                            }
                                        }
                                    }))}
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Font Family</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    value={settings.branding.theme.fontFamily}
                                    onChange={(e) => setSettings(prev => ({
                                        ...prev,
                                        branding: {
                                            ...prev.branding,
                                            theme: {
                                                ...prev.branding.theme,
                                                fontFamily: e.target.value
                                            }
                                        }
                                    }))}
                                >
                                    {fontFamilies.map((font) => (
                                        <MenuItem key={font} value={font}>
                                            {font}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        </Grid>
                    </Grid>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Welcome Heading"
                                value={settings.branding.landingPage.defaultWelcomeHeading}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        landingPage: {
                                            ...prev.branding.landingPage,
                                            defaultWelcomeHeading: e.target.value
                                        }
                                    }
                                }))}
                                sx={{ mb: 3 }}
                            />

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Welcome Message"
                                value={settings.branding.landingPage.defaultWelcomeMessage}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        landingPage: {
                                            ...prev.branding.landingPage,
                                            defaultWelcomeMessage: e.target.value
                                        }
                                    }
                                }))}
                                sx={{ mb: 3 }}
                            />

                            <TextField
                                fullWidth
                                label="Download Button Text"
                                value={settings.branding.landingPage.defaultButtonText}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        landingPage: {
                                            ...prev.branding.landingPage,
                                            defaultButtonText: e.target.value
                                        }
                                    }
                                }))}
                                sx={{ mb: 3 }}
                            />

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Footer Text"
                                value={settings.branding.landingPage.defaultFooterText}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    branding: {
                                        ...prev.branding,
                                        landingPage: {
                                            ...prev.branding.landingPage,
                                            defaultFooterText: e.target.value
                                        }
                                    }
                                }))}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Google Analytics Integration
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Configure Google Analytics or Google Tag Manager to track user interactions and gain insights into your QR code campaigns.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ mb: 3 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item>
                                        <Typography variant="subtitle1">Enable Analytics</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Switch
                                            checked={settings.analytics?.enabled || false}
                                            onChange={(e) => setSettings(prev => ({
                                                ...prev,
                                                analytics: {
                                                    ...prev.analytics,
                                                    enabled: e.target.checked
                                                }
                                            }))}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        {settings.analytics?.enabled && (
                            <>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Analytics Type</InputLabel>
                                        <Select
                                            value={settings.analytics?.type || 'google_analytics'}
                                            onChange={(e) => setSettings(prev => ({
                                                ...prev,
                                                analytics: {
                                                    ...prev.analytics,
                                                    type: e.target.value
                                                }
                                            }))}
                                            label="Analytics Type"
                                        >
                                            <MenuItem value="google_analytics">Google Analytics (GA4)</MenuItem>
                                            <MenuItem value="google_tag_manager">Google Tag Manager</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {settings.analytics?.type === 'google_analytics' && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Measurement ID"
                                            placeholder="G-XXXXXXXXXX"
                                            value={settings.analytics?.googleAnalytics?.measurementId || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                setSettings(prev => ({
                                                    ...prev,
                                                    analytics: {
                                                        ...prev.analytics,
                                                        googleAnalytics: {
                                                            measurementId: value
                                                        },
                                                        googleTagManager: {
                                                            containerId: ''
                                                        }
                                                    }
                                                }));
                                            }}
                                            error={!!settings.analytics?.googleAnalytics?.measurementId && !/^G-[A-Z0-9]+$/.test(settings.analytics.googleAnalytics.measurementId)}
                                            helperText={
                                                settings.analytics?.googleAnalytics?.measurementId && 
                                                !/^G-[A-Z0-9]+$/.test(settings.analytics.googleAnalytics.measurementId) 
                                                    ? "Invalid format. Must start with 'G-' followed by numbers and uppercase letters"
                                                    : "Enter your Google Analytics 4 Measurement ID (Format: G-XXXXXXXXXX)"
                                            }
                                        />
                                    </Grid>
                                )}

                                {settings.analytics?.type === 'google_tag_manager' && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Container ID"
                                            placeholder="GTM-XXXXXXXXXX"
                                            value={settings.analytics?.googleTagManager?.containerId || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                setSettings(prev => ({
                                                    ...prev,
                                                    analytics: {
                                                        ...prev.analytics,
                                                        googleTagManager: {
                                                            containerId: value
                                                        },
                                                        googleAnalytics: {
                                                            measurementId: ''
                                                        }
                                                    }
                                                }));
                                            }}
                                            error={!!settings.analytics?.googleTagManager?.containerId && !/^GTM-[A-Z0-9]+$/.test(settings.analytics.googleTagManager.containerId)}
                                            helperText={
                                                settings.analytics?.googleTagManager?.containerId && 
                                                !/^GTM-[A-Z0-9]+$/.test(settings.analytics.googleTagManager.containerId)
                                                    ? "Invalid format. Must start with 'GTM-' followed by numbers and uppercase letters"
                                                    : "Enter your Google Tag Manager Container ID (Format: GTM-XXXXXXXXXX)"
                                            }
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        {settings.analytics?.type === 'google_analytics' ? (
                                            <>
                                                To find your Measurement ID:
                                                <ol>
                                                    <li>Go to Google Analytics</li>
                                                    <li>Click Admin (gear icon)</li>
                                                    <li>Under Property, click Data Streams</li>
                                                    <li>Select or create a Web stream</li>
                                                    <li>Your Measurement ID will be displayed (Format: G-XXXXXXXXXX)</li>
                                                </ol>
                                            </>
                                        ) : (
                                            <>
                                                To find your Container ID:
                                                <ol>
                                                    <li>Go to Google Tag Manager</li>
                                                    <li>Click Admin</li>
                                                    <li>Under Container, find Container ID</li>
                                                    <li>Your Container ID will be displayed (Format: GTM-XXXXXXXXXX)</li>
                                                </ol>
                                            </>
                                        )}
                                    </Alert>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </TabPanel>
            </Paper>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ minWidth: 120 }}
                >
                    {saving ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
            </Box>

            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
            >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
}
